// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useExploreLive.ts
// 🔧 PURPOSE: Il dato vivo dei sei riquadri di Esplora, in un hook solo.
//   I riquadri non devono sapere da dove arriva il numero che mostrano:
//   qui si legge meteo (rete, cache 30 min), treni (cache del lib, 60s) e
//   quello che l'ospite ha già sul telefono (planner, wallet, note).
//   Ogni fonte è in try/catch e torna `null` quando manca: un riquadro
//   senza dato mostra solo nome e sottotitolo, mai un errore.
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchTrainBoard, upcomingDepartures, TrainBoard, TrainDeparture } from '../lib/trains';
import { useGeolocation, getDistanceMeters } from './useGeolocation';
import { romeToday } from '../components/DayChips';
import { BEACH_POIS } from '../data/mapPois';
import type { PlannerData, PlannerActivity } from './useVacationPlanner';
import {
  FALLBACK_RATES,
  STORAGE_KEY_RATES,
  WalletData,
  readRateCode,
} from '../pages/travel-wallet/types';
import {
  LEGACY_PLANNER_KEY,
  LEGACY_WALLET_KEY,
  plannerStorageKey,
  spentOn,
  storedBookingRef,
  totalSpent,
  walletStorageKey,
} from '../lib/budgetMath';

// --- Coordinate di Mola di Bari: il meteo di Esplora è sempre quello del paese,
// --- mai quello della posizione dell'ospite (che qui non si chiede).
// --- Le usa anche la Home (useHomeWeather), per avere lo stesso meteo.
export const MOLA_LAT = 41.0614;
export const MOLA_LON = 17.0877;

const WEATHER_CACHE_KEY = 'nva-explore-weather';
const WEATHER_TTL_MS = 30 * 60 * 1000;
/** Tornando sulla pagina si ricarica al massimo una volta al minuto. */
const REFRESH_THROTTLE_MS = 60 * 1000;
const NOTES_KEY = 'travel-notes';

export type WindLevel = 'light' | 'moderate' | 'strong';

export interface WeatherSummary {
  temp: number;
  /** Chiave sotto `weather.*` già usata da WeatherApp (clearSky, rain, ...). */
  labelKey: string;
  wind: WindLevel;
  seaTemp: number | null;
}

export interface TrainsSummary {
  next: TrainDeparture | null;
  /** La partenza dopo `next`, preferita nella direzione opposta. */
  then: TrainDeparture | null;
  nextByDirection: { north: TrainDeparture | null; south: TrainDeparture | null };
  /**
   * Tutte le partenze che restano, in ordine di orario (`next` è la prima).
   * Serve alle città raggiunte solo da alcuni treni (Trani: quelli per
   * Barletta), che cercano la prima partenza con la destinazione giusta.
   */
  upcoming: TrainDeparture[];
}

export interface PlannerSummary {
  count: number;
  nextName: string | null;
  nextTime: string | null;
}

export interface WalletSummary {
  /** Speso oggi in euro; null se oggi non c'è nessuna spesa. */
  todayEur: number | null;
  rateCode: string;
  rateValue: number | null;
  budgetSpentEur: number | null;
  budgetTotalEur: number | null;
}

export interface MapSummary {
  beachMeters: number;
}

export interface NotesSummary {
  count: number;
  lastTitle: string | null;
}

export interface ExploreLive {
  weather: WeatherSummary | null;
  trains: TrainsSummary | null;
  planner: PlannerSummary | null;
  wallet: WalletSummary | null;
  map: MapSummary | null;
  notes: NotesSummary | null;
  refresh: () => void;
}

// --- Meteo -------------------------------------------------------------

/** Codice WMO → stessa etichetta di WeatherApp (chiavi `weather.*` già tradotte). */
export function weatherLabelKey(code: number): string {
  if (code === 0) return 'clearSky';
  if (code === 1) return 'partlyCloudy';
  if (code === 2) return 'cloudy';
  if (code === 3) return 'overcast';
  if (code <= 49) return 'foggy';
  if (code <= 59) return 'drizzle';
  if (code <= 69) return 'rain';
  if (code <= 79) return 'snow';
  if (code <= 84) return 'showers';
  if (code <= 94) return 'snowShowers';
  return 'thunderstorm';
}

function windLevel(kmh: number): WindLevel {
  if (kmh < 15) return 'light';
  if (kmh < 30) return 'moderate';
  return 'strong';
}

function readWeatherCache(): WeatherSummary | null {
  try {
    const raw = localStorage.getItem(WEATHER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { fetchedAt?: number; data?: WeatherSummary };
    if (!parsed?.data || typeof parsed.fetchedAt !== 'number') return null;
    if (Date.now() - parsed.fetchedAt > WEATHER_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeWeatherCache(data: WeatherSummary): void {
  try {
    localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data }));
  } catch {
    /* storage pieno o bloccato: il meteo si ripesca la prossima volta */
  }
}

/**
 * Il meteo di adesso a Mola (temperatura, etichetta, vento) e la
 * temperatura del mare, dalla cache di 30 minuti o dalla rete. null se non
 * risponde. Esplora e la Home leggono la stessa cache.
 */
export async function loadWeather(): Promise<WeatherSummary | null> {
  const cached = readWeatherCache();
  if (cached) return cached;

  try {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${MOLA_LAT}&longitude=${MOLA_LON}` +
        '&current=temperature_2m,weather_code,wind_speed_10m&timezone=Europe%2FRome'
    );
    if (!res.ok) return null;
    const json = await res.json();
    const current = json?.current;
    if (!current || typeof current.temperature_2m !== 'number') return null;

    // Temperatura del mare: se l'endpoint marino non risponde il riquadro
    // mostra lo stesso la riga grande, senza "Mare ...".
    let seaTemp: number | null = null;
    try {
      const seaRes = await fetch(
        `https://marine-api.open-meteo.com/v1/marine?latitude=${MOLA_LAT}&longitude=${MOLA_LON}` +
          '&current=sea_surface_temperature&timezone=Europe%2FRome'
      );
      if (seaRes.ok) {
        const seaJson = await seaRes.json();
        const value = seaJson?.current?.sea_surface_temperature;
        if (typeof value === 'number') seaTemp = Math.round(value);
      }
    } catch {
      /* mare non disponibile: si ignora */
    }

    const summary: WeatherSummary = {
      temp: Math.round(current.temperature_2m),
      labelKey: weatherLabelKey(Number(current.weather_code) || 0),
      wind: windLevel(Number(current.wind_speed_10m) || 0),
      seaTemp,
    };
    writeWeatherCache(summary);
    return summary;
  } catch {
    return null;
  }
}

// --- Treni -------------------------------------------------------------

/**
 * Il riassunto del tabellone a un certo istante: la prossima partenza, quella
 * dopo e la prima per ciascuna direzione. Lo usano Esplora (qui sotto) e le
 * pagine città, che lo ricalcolano ogni 30 secondi sullo stesso tabellone.
 */
export function summarizeTrains(board: TrainBoard | null, now: number): TrainsSummary | null {
  const list = upcomingDepartures(board, now);
  if (list.length === 0) return null;

  const next = list[0];
  // La seconda riga preferisce l'altra direzione: due treni per Bari di
  // fila dicono meno di "verso Bari e verso Lecce".
  const opposite = list.slice(1).filter((d) => d.direction !== next.direction)[0];
  const then = opposite || list[1] || null;

  const firstOf = (dir: 'north' | 'south') =>
    list.filter((d) => (dir === 'north' ? d.direction === 'north' : d.direction !== 'north'))[0] || null;

  return { next, then, nextByDirection: { north: firstOf('north'), south: firstOf('south') }, upcoming: list };
}

async function loadTrains(): Promise<TrainsSummary | null> {
  try {
    const board = await fetchTrainBoard();
    return summarizeTrains(board, Date.now());
  } catch {
    return null;
  }
}

// --- Planner e Portafoglio (localStorage, chiave della prenotazione) ----

/**
 * I dati dei due strumenti stanno sotto la prenotazione (`nva-planner:<ref>`).
 * Chi non è ancora entrato ha scritto sotto `guest`, e chi non ha mai aperto
 * gli strumenti riscritti ha ancora solo la chiave vecchia: si guarda nei tre
 * posti, in quest'ordine, e vale il primo che risponde.
 */
function readLocal<T>(keys: string[]): T | null {
  for (let i = 0; i < keys.length; i += 1) {
    try {
      const raw = localStorage.getItem(keys[i]);
      if (raw) return JSON.parse(raw) as T;
    } catch {
      /* chiave illeggibile: si prova la successiva */
    }
  }
  return null;
}

function loadPlanner(): PlannerSummary | null {
  const ref = storedBookingRef();
  const keys = [plannerStorageKey(ref)];
  if (ref !== 'guest') keys.push(plannerStorageKey('guest'));
  keys.push(LEGACY_PLANNER_KEY);

  const data = readLocal<Partial<PlannerData>>(keys);
  const day = data?.days?.[romeToday()];
  const activities: PlannerActivity[] = Array.isArray(day?.activities) ? (day as { activities: PlannerActivity[] }).activities : [];
  if (activities.length === 0) return null;

  const nowTime = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());

  // `done` e' il campo nuovo, `completed` quello della chiave vecchia.
  const isDone = (a: PlannerActivity & { completed?: boolean }) => Boolean(a.done || a.completed);

  const upcoming = activities
    .filter((a) => !isDone(a) && typeof a.time === 'string' && /^\d{2}:\d{2}$/.test(a.time) && (a.time as string) >= nowTime)
    .sort((a, b) => String(a.time).localeCompare(String(b.time)))[0];

  return {
    count: activities.length,
    nextName: upcoming?.name || null,
    nextTime: upcoming?.time || null,
  };
}

function loadWallet(): WalletSummary | null {
  let rates: Record<string, number> = FALLBACK_RATES;
  try {
    const cached = localStorage.getItem(STORAGE_KEY_RATES);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') rates = parsed as Record<string, number>;
    }
  } catch {
    /* cache tassi illeggibile: restano quelli di riserva */
  }
  // La valuta della riga "1 € = ..." e' l'ultima scelta nel cambio.
  const rateCode = readRateCode();
  const rateValue = typeof rates[rateCode] === 'number' ? rates[rateCode] : null;

  const ref = storedBookingRef();
  const keys = [walletStorageKey(ref)];
  if (ref !== 'guest') keys.push(walletStorageKey('guest'));

  let todayEur: number | null = null;
  let budgetSpentEur: number | null = null;
  let budgetTotalEur: number | null = null;

  const data = readLocal<Partial<WalletData>>(keys);
  if (data) {
    const expenses = Array.isArray(data.expenses) ? data.expenses : [];
    const spentToday = spentOn(expenses, romeToday());
    if (spentToday > 0) todayEur = spentToday;
    const total = Number(data.budget?.amountEur);
    if (Number.isFinite(total) && total > 0) {
      budgetTotalEur = total;
      // Speso = tutte le spese, come nella card del Portafoglio.
      budgetSpentEur = totalSpent(expenses);
    }
  } else {
    // Portafoglio mai aperto dopo la riscrittura: la chiave vecchia e'
    // l'unica che sa quanto si e' speso oggi (importi gia' in euro).
    const legacy = readLocal<{
      budget?: { totalBudgetEur?: number } | null;
      expenses?: Array<{ amount?: number; date?: string }>;
    }>([LEGACY_WALLET_KEY]);
    const expenses = Array.isArray(legacy?.expenses) ? legacy!.expenses! : [];
    const romeDayOf = (iso: string): string => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    };
    const today = romeToday();
    const spentToday = expenses
      .filter((e) => romeDayOf(String(e?.date || '')) === today)
      .reduce((sum, e) => sum + (Number(e?.amount) || 0), 0);
    if (spentToday > 0) todayEur = spentToday;
    const total = Number(legacy?.budget?.totalBudgetEur);
    if (Number.isFinite(total) && total > 0) {
      budgetTotalEur = total;
      budgetSpentEur = expenses.reduce((sum, e) => sum + (Number(e?.amount) || 0), 0);
    }
  }

  if (todayEur === null && budgetTotalEur === null && rateValue === null) return null;
  return { todayEur, rateCode, rateValue, budgetSpentEur, budgetTotalEur };
}

// --- Note --------------------------------------------------------------

function loadNotes(): NotesSummary | null {
  try {
    const raw = localStorage.getItem(NOTES_KEY);
    if (!raw) return null;
    const list = JSON.parse(raw);
    if (!Array.isArray(list) || list.length === 0) return null;

    const timeOf = (n: { updatedAt?: string; date?: string }) => {
      const value = new Date(n?.updatedAt || n?.date || 0).getTime();
      return Number.isNaN(value) ? 0 : value;
    };
    const last = list.slice().sort((a, b) => timeOf(b) - timeOf(a))[0];
    const title = String(last?.title || '').trim() || String(last?.content || '').trim().slice(0, 28);

    return { count: list.length, lastTitle: title || null };
  } catch {
    return null;
  }
}

// --- Hook --------------------------------------------------------------

export function useExploreLive(): ExploreLive {
  const { position } = useGeolocation(); // solo la posizione già concessa: da Esplora non si chiede mai
  const [weather, setWeather] = useState<WeatherSummary | null>(null);
  const [trains, setTrains] = useState<TrainsSummary | null>(null);
  const [planner, setPlanner] = useState<PlannerSummary | null>(() => loadPlanner());
  const [wallet, setWallet] = useState<WalletSummary | null>(() => loadWallet());
  const [notes, setNotes] = useState<NotesSummary | null>(() => loadNotes());
  const lastRefreshRef = useRef(0);
  const aliveRef = useRef(true);

  const refresh = useCallback(() => {
    lastRefreshRef.current = Date.now();
    // Il locale è sincrono: si rilegge sempre (costa una JSON.parse).
    setPlanner(loadPlanner());
    setWallet(loadWallet());
    setNotes(loadNotes());
    loadWeather().then((value) => { if (aliveRef.current && value) setWeather(value); });
    loadTrains().then((value) => { if (aliveRef.current) setTrains(value); });
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    refresh();
    // Tornando sull'app si riprende il filo, ma non più di una volta al minuto.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshRef.current < REFRESH_THROTTLE_MS) return;
      refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      aliveRef.current = false;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Mappe: distanza dalla spiaggia più vicina, solo se la posizione c'è già.
  const map = useMemo<MapSummary | null>(() => {
    if (!position || BEACH_POIS.length === 0) return null;
    const distances = BEACH_POIS.map((poi) => getDistanceMeters(position.lat, position.lon, poi.lat, poi.lon));
    return { beachMeters: Math.min.apply(null, distances) };
  }, [position]);

  return { weather, trains, planner, wallet, map, notes, refresh };
}

export default useExploreLive;
